import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
// @ts-ignore Ignore unused commented imports
import { CoreActions } from '@next/core-lib';
import { NotificationActions } from '@next/core-lib/notification';
import { ofType, Actions, createEffect } from '@ngrx/effects';
// @ts-ignore Ignore unused commented ActionTypes import
import { Action, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import {
  exhaustMap,
  catchError,
  switchMap,
  startWith,
  finalize,
  mergeMap,
  withLatestFrom,
  map,
} from 'rxjs/operators';

import * as cycleActions from '../../../cycle/store/actions/cycle.actions';
import * as settingActions from '../../../settings/store/actions/setting.actions';
import { DeliveryModuleState, Delivery } from '../../models/delivery.model';
import { FlockRegistrationDeliveryService } from '../../services/flock-registration-delivery.service';
import * as deliveryActions from '../actions/delivery.actions';
import { getDeliveryModuleState } from '../selectors/delivery.selectors';

@Injectable()
export class DeliveryEffects {
  private actions$ = inject(Actions);
  private store = inject<Store<DeliveryModuleState>>(Store);
  private flockRegistrationDeliveryService = inject(FlockRegistrationDeliveryService);

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {}

  loadDeliveries$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(deliveryActions.loadDeliveriesRequest),
      withLatestFrom(this.store.select(getDeliveryModuleState)),
      mergeMap(([{ startDate, endDate }, state]) => {
        if (startDate === undefined) {
          startDate = state.startDate;
        }
        if (endDate === undefined) {
          endDate = state.endDate;
        }

        return this.flockRegistrationDeliveryService.getDeliveries(startDate, endDate).pipe(
          switchMap((deliveries: Delivery[]) =>
            of(deliveries.sort((d1: Delivery, d2: Delivery) => d2.id - d1.id)),
          ),
          switchMap((deliveries) =>
            of(
              settingActions.loadFinalSetupSettings(),
              deliveryActions.loadDeliveriesSuccess({ deliveries }),
            ),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              deliveryActions.loadDeliveriesFailed({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        );
      }),
    ),
  );

  upsertActiveDeliveriesRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(deliveryActions.upsertActiveDeliveriesRequest),
      exhaustMap(({ activeDelivery }) =>
        this.flockRegistrationDeliveryService.upsertActiveDeliveries(activeDelivery).pipe(
          switchMap((_) =>
            of(deliveryActions.upsertActiveDeliveriesSuccess({ delivery: activeDelivery })),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              deliveryActions.loadDeliveriesRequest({}),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  upsertManualDeliveryRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(deliveryActions.upsertManualDeliveryRequest),
      exhaustMap(({ manualDeliveryRequest }) =>
        this.flockRegistrationDeliveryService.upsertManualDelivery(manualDeliveryRequest).pipe(
          switchMap((_) =>
            of(
              deliveryActions.upsertManualDeliverySuccess(),
              deliveryActions.loadDeliveriesRequest({}),
            ),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              deliveryActions.upsertManualDeliveryFailed({ error }),
              deliveryActions.loadDeliveriesRequest({}),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
        ),
      ),
    ),
  );

  removeManualDeliveryRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(deliveryActions.removeManualDeliveryRequest),
      exhaustMap(({ manualDeliveryId }) =>
        this.flockRegistrationDeliveryService.removeManualDelivery(manualDeliveryId).pipe(
          switchMap((_) =>
            of(
              deliveryActions.removeManualDeliverySuccess(),
              deliveryActions.loadDeliveriesRequest({}),
            ),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              deliveryActions.removeManualDeliveryFailed({ error }),
              deliveryActions.loadDeliveriesRequest({}),
              cycleActions.loadDeliveriesRequest(),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
        ),
      ),
    ),
  );

  setDeliveriesFilter$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(deliveryActions.setDeliveriesFilter),
      switchMap(({ startDate, endDate }) =>
        of(deliveryActions.loadDeliveriesRequest({ startDate, endDate })),
      ),
    ),
  );

  upsertShowInFinalSetupDeliveriesRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(deliveryActions.upsertShowInFinalSetupDeliveriesRequest),
      exhaustMap(({ request }) =>
        this.flockRegistrationDeliveryService.upsertShowInFinalSetupDeliveries(request).pipe(
          map((upsertedDeliveryId) =>
            deliveryActions.upsertShowInFinalSetupDeliveriesSuccess({
              request: {
                deliveryId: upsertedDeliveryId,
                deliveryLineExternalUniqueIdentifier: request.deliveryLineExternalUniqueIdentifier,
                showInFinalSetup: request.showInFinalSetup,
              },
            }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              deliveryActions.loadDeliveriesRequest({}),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );
}
